import React, { useState, useEffect, useCallback, useDeferredValue } from 'react'
import {
	Box,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	IconButton,
	useMediaQuery,
	useTheme,
	Menu,
	MenuItem
} from '@mui/material'
import {
	Add as AddIcon,
	Search as SearchIcon,
	MoreVert as MoreVertIcon,
	UploadFile as UploadFileIcon
} from '@mui/icons-material'
import { supabase } from "../utils/supabase"
import { getFullName } from '../utils/clientUtils'
import { fetchPetAvatarsBatch, deleteAllClientPetPhotos } from '../utils/petPhotoUtils'
import ClientDetailsDialog from './ClientDetailsDialog'
import AddEditClientDialog from './AddEditClientDialog'
import AddAppointmentDialog from './AddAppointmentDialog'
import ImportClientsPetsDialog from './ImportClientsPetsDialog'
import PetAvatar from './PetAvatar'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

// Nami lavender theme tokens (from the design handoff)
const FONT = "'Outfit', sans-serif"
const CM = {
	bg: '#FDFCFF',
	page: '#FBFAFE',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	field: '#FFFFFF',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	onPrimary: '#FFFFFF',
	danger: '#BE4B45',
	dangerSoft: '#FBECEB',
	headBg: '#EFEDF6',
	headText: '#6E6688'
}

function ClientsManager({ merchant, onMessageClient, preselectedClient, focusPetId, onPreselectConsumed }) {
	const [clients, setClients] = useState([])
	const [loading, setLoading] = useState(false)
	const [dialogOpen, setDialogOpen] = useState(false)
	const [editingClient, setEditingClient] = useState(null)
	const [error, setError] = useState('')
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
	const [clientToDelete, setClientToDelete] = useState(null)
	const [clientPets, setClientPets] = useState({})
	const [petAvatars, setPetAvatars] = useState({}) // Map of petId -> avatarUrl
	const [searchText, setSearchText] = useState('')
	const [clientDetailsOpen, setClientDetailsOpen] = useState(false)
	const [selectedClient, setSelectedClient] = useState(null)
	// Pet card to scroll to / highlight once the profile opens (set when we
	// arrive here from an appointment's pet card).
	const [focusPet, setFocusPet] = useState(null)
	const [moreMenuAnchor, setMoreMenuAnchor] = useState(null)
	const [moreMenuClient, setMoreMenuClient] = useState(null)
	const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false)
	const [appointmentClient, setAppointmentClient] = useState(null)
	const [importDialogOpen, setImportDialogOpen] = useState(false)

	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))


	const loadClients = useCallback(async () => {
		setLoading(true)
		try {
			const { data, error } = await supabase
				.from('clients')
				.select('*')
				.eq('merchant_id', merchant.id)
				.order('name')

			if (error) throw error

			const clientsData = data || []
			setClients(clientsData)

			// Load pets for all clients
			if (clientsData.length > 0) {
				const clientIds = clientsData.map(client => client.id)

				// Load pets
				const { data: petsData, error: petsError } = await supabase
					.from('pets')
					.select('*')
					.in('client_id', clientIds)
					.order('name')

				if (petsError) {
					console.error('Error loading pets:', petsError)
				} else {
					// Group pets by client_id
					const petsByClient = {}
					const allPetIds = []
					petsData?.forEach(pet => {
						if (!petsByClient[pet.client_id]) {
							petsByClient[pet.client_id] = []
						}
						petsByClient[pet.client_id].push(pet)
						allPetIds.push(pet.id)
					})
					setClientPets(petsByClient)

					// Batch fetch all pet avatars in one DB call
					if (allPetIds.length > 0) {
						const avatarMap = await fetchPetAvatarsBatch(allPetIds)
						setPetAvatars(avatarMap)
					}
				}
			}
		} catch (e) {
			console.error('Error loading clients:', e)
			setError('Failed to load clients')
		} finally {
			setLoading(false)
		}
	}, [merchant?.id])

	useEffect(() => {
		if (merchant?.id) {
			loadClients()
		}
	}, [merchant, loadClients])


	const handleAddClient = () => {
		setEditingClient(null)
		setDialogOpen(true)
	}

	const handleEditClient = (client) => {
		setEditingClient(client)
		setDialogOpen(true)
	}

	const handleDeleteClient = (client) => {
		setClientToDelete(client)
		setDeleteConfirmOpen(true)
	}

	const confirmDelete = async () => {
		if (!clientToDelete) return

		try {
			// Delete all pet photos for this client's pets from storage and database first
			await deleteAllClientPetPhotos(clientToDelete.id)

			const { error } = await supabase
				.from('clients')
				.delete()
				.eq('id', clientToDelete.id)

			if (error) throw error

			setClients(prev => prev.filter(c => c.id !== clientToDelete.id))
			setDeleteConfirmOpen(false)
			setClientToDelete(null)
		} catch (e) {
			console.error('Error deleting client:', e)
			setError('Failed to delete client')
		}
	}

	// Search functionality
	const filterClients = (clients, searchCriteria) => {
		if (!searchCriteria || !searchCriteria.text) {
			return clients
		}

		const { text, type } = searchCriteria
		const searchTerm = text.toLowerCase()

		return clients.filter(client => {
			const fullName = getFullName(client).toLowerCase()
			switch (type) {
				case 'name':
					return fullName.includes(searchTerm)
				case 'email':
					return client.email?.toLowerCase().includes(searchTerm)
				case 'phone':
					return client.phone_number?.toLowerCase().includes(searchTerm)
				case 'all':
				default:
					return (
						fullName.includes(searchTerm) ||
						client.email?.toLowerCase().includes(searchTerm) ||
						client.phone_number?.toLowerCase().includes(searchTerm) ||
						client.address?.toLowerCase().includes(searchTerm)
					)
			}
		})
	}

	const clearSearch = () => {
		setSearchText('')
	}

	const handleSearchKeyDown = (e) => {
		if (e.key === 'Escape') {
			e.preventDefault()
			clearSearch()
		}
	}

	// Search field — filters as you type; the ✕ clears it (as does Escape).
	const renderSearch = (mobile) => (
		<Box sx={{
			display: 'flex',
			alignItems: 'center',
			gap: '8px',
			width: mobile ? '100%' : '280px',
			...(mobile ? { mt: '12px' } : {})
		}}>
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '8px',
				height: mobile ? '42px' : '40px',
				flex: 1,
				minWidth: 0,
				px: mobile ? '12px' : '13px',
				borderRadius: mobile ? '11px' : '10px',
				bgcolor: mobile ? CM.surface : CM.field,
				border: mobile ? 'none' : `1.5px solid ${CM.border}`,
				color: CM.muted,
				'&:focus-within': mobile ? {} : { borderColor: CM.primary }
			}}>
				<SearchIcon sx={{ fontSize: 16, flexShrink: 0 }} />
				<Box
					component="input"
					type="text"
					value={searchText}
					placeholder="Search clients"
					aria-label="Search clients"
					onChange={(e) => setSearchText(e.target.value)}
					onKeyDown={handleSearchKeyDown}
					sx={{
						flex: 1,
						minWidth: 0,
						p: 0,
						border: 'none',
						outline: 'none',
						bgcolor: 'transparent',
						fontFamily: mobile ? 'Inter' : FONT,
						fontSize: mobile ? '15px' : '13.5px',
						fontWeight: 600,
						color: CM.text,
						'&::placeholder': { color: CM.muted, fontWeight: 500, opacity: 1 }
					}}
				/>
				{searchText && (
					<Box
						component="span"
						role="button"
						aria-label="Clear search"
						onClick={clearSearch}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 20,
							height: 20,
							flexShrink: 0,
							borderRadius: '50%',
							color: CM.muted,
							cursor: 'pointer',
							'&:hover': { bgcolor: CM.border, color: CM.text }
						}}
					>
						<svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" xmlns="http://www.w3.org/2000/svg">
							<path d="M4 4l8 8M12 4l-8 8" />
						</svg>
					</Box>
				)}
			</Box>
		</Box>
	)

	// Determine which clients to display
	// Derived, not stored — so results stay correct when a client is added,
	// edited or deleted while a search is active.
	//
	// Search runs as you type. It filters the already-loaded list in memory (no
	// round trip), and the term is deferred so a long list re-rendering never
	// makes the field feel laggy — the input stays on the latest keystroke while
	// the results catch up.
	const deferredSearch = useDeferredValue(searchText)
	const searchCriteria = deferredSearch.trim()
		? { text: deferredSearch.trim(), type: 'all' }
		: null
	const displayedClients = searchCriteria ? filterClients(clients, searchCriteria) : clients

	const handleClientClick = (client) => {
		setSelectedClient(client)
		setClientDetailsOpen(true)
	}

	// Opened from elsewhere (an appointment's client / pet card): jump straight
	// into that client's profile once their row has loaded.
	useEffect(() => {
		if (!preselectedClient?.id) return
		const full = clients.find(c => c.id === preselectedClient.id)
		if (!full && clients.length === 0) return
		setSelectedClient(full || preselectedClient)
		setFocusPet(focusPetId || null)
		setClientDetailsOpen(true)
		onPreselectConsumed && onPreselectConsumed()
	}, [preselectedClient, focusPetId, clients, onPreselectConsumed])

	const handleCloseClientDetails = () => {
		setClientDetailsOpen(false)
		setSelectedClient(null)
		setFocusPet(null)
	}

	const handleMoreClick = (event, client) => {
		event.stopPropagation()
		setMoreMenuAnchor(event.currentTarget)
		setMoreMenuClient(client)
	}

	const handleMoreClose = () => {
		setMoreMenuAnchor(null)
		setMoreMenuClient(null)
	}

	const handleEditFromMenu = () => {
		if (moreMenuClient) {
			handleEditClient(moreMenuClient)
		}
		handleMoreClose()
	}

	const handleClientSaved = ({ client, pets, isEdit }) => {
		if (isEdit) {
			// Update existing client in the list
			setClients(prev => prev.map(c =>
				c.id === client.id ? client : c
			))
		} else {
			// Add new client to the list
			setClients(prev => [...prev, client].sort((a, b) => getFullName(a).localeCompare(getFullName(b))))
		}

		// Update pets for this client
		setClientPets(prev => ({
			...prev,
			[client.id]: pets
		}))

		setDialogOpen(false)
	}

	const handleAddBooking = (client) => {
		setAppointmentClient(client)
		setAppointmentDialogOpen(true)
	}

	const handleAppointmentCreated = (appointment) => {
		// Optionally handle the created appointment (e.g., show success message)
		console.log('Appointment created:', appointment)
	}

	const handleMessageClient = (client) => {
		if (onMessageClient) {
			onMessageClient(client)
		}
	}

	const handlePetsUpdated = (clientId, updatedPets) => {
		// Update the clientPets state with the new pets list
		setClientPets(prev => ({
			...prev,
			[clientId]: updatedPets
		}))

		// Re-fetch avatars for every pet in this client, not just ones we have no
		// avatar for — a pet whose photo was replaced already has a (now stale)
		// cached URL, and skipping it left the old photo on the list until reload.
		const petIds = updatedPets.map(pet => pet.id)
		if (petIds.length === 0) return

		fetchPetAvatarsBatch(petIds).then(freshAvatars => {
			setPetAvatars(prev => {
				const next = { ...prev }
				// Drop the old entries first so a removed photo actually disappears
				// (the batch fetch only returns pets that still have an avatar).
				petIds.forEach(id => { delete next[id] })
				return { ...next, ...freshAvatars }
			})
		})
	}

	// Photo changes on an existing pet are uploaded immediately by PetAvatar, so
	// the cached URL goes stale even if the user backs out without saving.
	const handlePetAvatarUpdated = (petId, url) => {
		if (!petId) return
		setPetAvatars(prev => {
			const next = { ...prev }
			if (url) next[petId] = url
			else delete next[petId]
			return next
		})
	}

	// If a client is selected, show the details page instead of the list
	if (clientDetailsOpen && selectedClient) {
		return (
			<ClientDetailsDialog
				onClose={handleCloseClientDetails}
				client={selectedClient}
				clientPets={selectedClient ? clientPets[selectedClient.id] : []}
				merchant={merchant}
				onMessageClient={handleMessageClient}
				onPetsUpdated={handlePetsUpdated}
				onPetAvatarUpdated={handlePetAvatarUpdated}
				focusPetId={focusPet}
			/>
		)
	}

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : 0, padding: isMobile ? '0px 20px 0px 20px' : '22px 26px 26px 26px', bgcolor: isMobile ? '#ffffff' : CM.page, boxSizing: 'border-box' }}>
			{/* Page Header */}
			{isMobile ? (
				/* Mobile Header — Nami "Clients & Pets" mobile list head */
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
						<Typography sx={{
							fontFamily: FONT,
							fontSize: '27px',
							fontWeight: 800,
							letterSpacing: '-0.02em',
							color: CM.text
						}}>
							Clients &amp; Pets
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<IconButton
								onClick={() => setImportDialogOpen(true)}
								aria-label="Import clients"
								sx={{
									width: 40,
									height: 40,
									flexShrink: 0,
									border: `1px solid ${CM.border}`,
									borderRadius: '11px',
									bgcolor: CM.field,
									color: CM.primaryDeep,
									'&:hover': { bgcolor: CM.surface }
								}}
							>
								<UploadFileIcon sx={{ fontSize: 18 }} />
							</IconButton>
							<Button
								onClick={handleAddClient}
								startIcon={<AddIcon sx={{ fontSize: 16 }} />}
								sx={{
									height: '40px',
									px: '15px',
									border: 'none',
									borderRadius: '11px',
									bgcolor: CM.primary,
									color: CM.onPrimary,
									textTransform: 'none',
									fontFamily: FONT,
									fontWeight: 700,
									fontSize: '14px',
									whiteSpace: 'nowrap',
									boxShadow: `0 2px 8px ${CM.primarySoft}`,
									'&:hover': { bgcolor: CM.primaryDeep },
									'& .MuiButton-startIcon': { marginRight: '6px' }
								}}
							>
								New
							</Button>
						</Box>
					</Box>

					<Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: CM.muted, mt: '2px' }}>
						{displayedClients.length} client{displayedClients.length === 1 ? '' : 's'}
					</Typography>

					{/* Search Bar - Full Width */}
					{renderSearch(true)}
				</Box>
			) : (
				/* Desktop Header */
				<Box sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '16px',
					minHeight: '40px',
					mb: '18px'
				}}>
					{/* Title + Import CSV */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						<Typography sx={{
							fontFamily: FONT,
							fontSize: '23px',
							fontWeight: 800,
							letterSpacing: '-0.02em',
							color: CM.text,
							whiteSpace: 'nowrap'
						}}>
							Clients &amp; Pets
						</Typography>
						<Button
							onClick={() => setImportDialogOpen(true)}
							startIcon={<UploadFileIcon sx={{ fontSize: 15 }} />}
							sx={{
								height: '40px',
								px: '16px',
								border: `1.5px solid ${CM.border}`,
								borderRadius: '10px',
								bgcolor: CM.field,
								textTransform: 'none',
								fontFamily: FONT,
								fontWeight: 650,
								fontSize: '13.5px',
								color: CM.text,
								whiteSpace: 'nowrap',
								'&:hover': { borderColor: CM.primary, color: CM.primaryDeep, bgcolor: CM.field },
								'& .MuiButton-startIcon': { marginRight: '7px' }
							}}
						>
							Import CSV
						</Button>
					</Box>

					{/* Search + New client */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						{renderSearch(false)}
						<Button
							onClick={handleAddClient}
							startIcon={<AddIcon sx={{ fontSize: 16 }} />}
							sx={{
								height: '40px',
								px: '16px',
								border: 'none',
								borderRadius: '10px',
								bgcolor: CM.primary,
								color: CM.onPrimary,
								textTransform: 'none',
								fontFamily: FONT,
								fontWeight: 700,
								fontSize: '13.5px',
								whiteSpace: 'nowrap',
								boxShadow: `0 2px 8px ${CM.primarySoft}`,
								'&:hover': { bgcolor: CM.primaryDeep },
								'& .MuiButton-startIcon': { marginRight: '7px' }
							}}
						>
							New client
						</Button>
					</Box>
				</Box>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{/* Content Container */}
			<Box sx={{
				flexGrow: 1,
				overflow: 'auto',
				padding: isMobile ? '0' : '0 !important',
				pb: isMobile ? '80px' : 0 // Space for FAB on mobile
			}}>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<Typography>Loading clients...</Typography>
					</Box>
				) : displayedClients.length === 0 ? (
					<Box sx={{
						bgcolor: CM.field,
						border: `1px solid ${CM.border}`,
						borderRadius: '14px',
						p: '28px 16px',
						textAlign: 'center',
						color: CM.muted,
						fontFamily: FONT,
						fontWeight: 600,
						fontSize: '14px',
						boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)'
					}}>
						{searchCriteria
							? `No clients match \u201c${searchCriteria.text}\u201d.`
							: 'No clients yet. Add your first client to get started.'}
					</Box>
				) : isMobile ? (
					/* Mobile Card View — Nami "cmb-card" list */
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '10px'
					}}>
						{displayedClients.map((client) => {
							const clientPetsList = clientPets[client.id] || []
							const petsWithAvatars = clientPetsList.filter(pet => petAvatars[pet.id])

							return (
								<Box
									key={client.id}
									onClick={() => handleClientClick(client)}
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: '9px',
										padding: '13px 14px',
										bgcolor: CM.field,
										border: `1px solid ${CM.border}`,
										borderRadius: '16px',
										boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)',
										cursor: 'pointer'
									}}
								>
									{/* Identity row */}
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
										<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
											<Typography sx={{
												fontFamily: 'Inter',
												fontWeight: 750,
												fontSize: '15.5px',
												letterSpacing: '-0.01em',
												color: CM.text,
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis'
											}}>
												{getFullName(client)}
											</Typography>
											{clientPetsList.length > 0 && (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
													{petsWithAvatars.length > 0 && (
														<Box sx={{ display: 'flex', flexShrink: 0 }}>
															{petsWithAvatars.slice(0, 3).map((pet, idx) => (
																<Box
																	key={pet.id}
																	sx={{ marginLeft: idx === 0 ? 0 : '-8px', zIndex: 3 - idx, position: 'relative', borderRadius: '50%', boxShadow: `0 0 0 2px ${CM.field}` }}
																>
																	<PetAvatar petId={pet.id} size={22} editable={false} hideIfEmpty={true} preloadedAvatarUrl={petAvatars[pet.id]} />
																</Box>
															))}
														</Box>
													)}
													<Typography sx={{
														fontFamily: 'Inter',
														fontWeight: 600,
														fontSize: '12.5px',
														color: CM.muted,
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis'
													}}>
														{clientPetsList.length > 3 ? `${clientPetsList.length} pets` : clientPetsList.map(p => p.name).join(', ')}
													</Typography>
												</Box>
											)}
										</Box>
										<IconButton
											onClick={(e) => handleMoreClick(e, client)}
											aria-label="More actions"
											sx={{ width: 34, height: 34, flexShrink: 0, color: CM.muted, '&:hover': { bgcolor: CM.surface, color: CM.text } }}
										>
											<MoreVertIcon sx={{ fontSize: 18 }} />
										</IconButton>
									</Box>

									{/* Contact meta row */}
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
										<Typography sx={{
											flex: 1,
											minWidth: 0,
											fontFamily: 'Inter',
											fontWeight: 600,
											fontSize: '12px',
											color: CM.muted,
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis'
										}}>
											{client.email || 'No email'}
										</Typography>
										<Typography sx={{
											flexShrink: 0,
											fontFamily: 'Inter',
											fontWeight: 600,
											fontSize: '12px',
											color: CM.muted,
											whiteSpace: 'nowrap'
										}}>
											{client.phone_number || 'No phone'}
										</Typography>
									</Box>
								</Box>
							)
						})}
					</Box>
				) : (
					/* Desktop Table View */
					<Box sx={{
						flex: 1,
						minWidth: 0,
						bgcolor: CM.field,
						border: `1px solid ${CM.border}`,
						borderRadius: '14px',
						overflow: 'hidden',
						boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)'
					}}>
						<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
							<Box component="thead">
								<Box component="tr">
									{['Client name', 'Pets', 'Phone', 'Email'].map((label) => (
										<Box
											component="th"
											key={label}
											sx={{
												textAlign: 'left',
												fontFamily: FONT,
												fontSize: '11px',
												fontWeight: 600,
												textTransform: 'uppercase',
												letterSpacing: '0.06em',
												color: CM.headText,
												p: '13px 16px',
												bgcolor: CM.headBg,
												borderBottom: `1.5px solid ${CM.border}`,
												whiteSpace: 'nowrap'
											}}
										>
											{label}
										</Box>
									))}
									<Box
										component="th"
										sx={{
											textAlign: 'right',
											width: '96px',
											fontFamily: FONT,
											fontSize: '11px',
											fontWeight: 600,
											textTransform: 'uppercase',
											letterSpacing: '0.06em',
											color: CM.headText,
											p: '13px 16px',
											bgcolor: CM.headBg,
											borderBottom: `1.5px solid ${CM.border}`,
											whiteSpace: 'nowrap'
										}}
									>
										Actions
									</Box>
								</Box>
							</Box>
							<Box component="tbody">
								{displayedClients.map((client, index) => {
									const clientPetsList = clientPets[client.id] || []
									const isLast = index === displayedClients.length - 1
									const cellSx = {
										p: '11px 16px',
										borderBottom: isLast ? 'none' : `1px solid ${CM.surface}`,
										verticalAlign: 'middle'
									}
									const petsWithAvatars = clientPetsList.filter((pet) => petAvatars[pet.id])

									return (
										<Box
											component="tr"
											key={client.id}
											onClick={() => handleClientClick(client)}
											sx={{ cursor: 'pointer', transition: 'background 0.12s', '&:hover': { bgcolor: CM.primarySoft } }}
										>
											{/* Client name */}
											<Box component="td" sx={cellSx}>
												<Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: CM.text, letterSpacing: '-0.01em' }}>
													{getFullName(client)}
												</Typography>
											</Box>

											{/* Pets */}
											<Box component="td" sx={cellSx}>
												{clientPetsList.length > 0 ? (
													<Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
														{petsWithAvatars.length > 0 && (
															<Box sx={{ display: 'flex' }}>
																{petsWithAvatars.slice(0, 3).map((pet, idx) => (
																	<Box
																		key={pet.id}
																		sx={{ marginLeft: idx === 0 ? 0 : '-8px', zIndex: 3 - idx, position: 'relative', borderRadius: '50%', boxShadow: `0 0 0 2px ${CM.field}` }}
																	>
																		<PetAvatar petId={pet.id} size={28} editable={false} hideIfEmpty={true} preloadedAvatarUrl={petAvatars[pet.id]} />
																	</Box>
																))}
																{petsWithAvatars.length > 3 && (
																	<Box sx={{ marginLeft: '-8px', zIndex: 0, width: 28, height: 28, borderRadius: '50%', bgcolor: CM.primarySoft, boxShadow: `0 0 0 2px ${CM.field}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
																		<Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '10px', color: CM.primaryDeep }}>
																			+{petsWithAvatars.length - 3}
																		</Typography>
																	</Box>
																)}
															</Box>
														)}
														<Typography sx={{ fontFamily: FONT, fontSize: '13.5px', fontWeight: 500, color: CM.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
															{clientPetsList.length > 3 ? `${clientPetsList.length} pets` : clientPetsList.map((p) => p.name).join(', ')}
														</Typography>
													</Box>
												) : (
													<Typography sx={{ fontFamily: FONT, fontSize: '13.5px', color: CM.muted }}>No pets</Typography>
												)}
											</Box>

											{/* Phone */}
											<Box component="td" sx={cellSx}>
												<Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 400, color: client.phone_number ? CM.muted : CM.muted }}>
													{client.phone_number || '\u2014'}
												</Typography>
											</Box>

											{/* Email */}
											<Box component="td" sx={cellSx}>
												<Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 400, color: CM.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>
													{client.email || '\u2014'}
												</Typography>
											</Box>

											{/* Actions */}
											<Box component="td" sx={{ ...cellSx, width: '96px' }} onClick={(e) => e.stopPropagation()}>
												<Box sx={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
													<IconButton
														onClick={() => handleEditClient(client)}
														aria-label="Edit"
														sx={{ width: 30, height: 30, border: `1px solid ${CM.border}`, borderRadius: '8px', bgcolor: CM.field, color: CM.muted, '&:hover': { color: CM.primaryDeep, borderColor: CM.primary, bgcolor: CM.field } }}
													>
														<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
															<path d="M11.3333 2A1.885 1.885 0 0114 4.667l-9 9L1.333 14.667 2.333 11l9-9z" />
														</svg>
													</IconButton>
													<IconButton
														onClick={() => handleDeleteClient(client)}
														aria-label="Delete"
														sx={{ width: 30, height: 30, border: `1px solid ${CM.border}`, borderRadius: '8px', bgcolor: CM.field, color: CM.muted, '&:hover': { color: CM.danger, borderColor: CM.danger, bgcolor: CM.dangerSoft } }}
													>
														<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
															<path d="M2 4h12M12.667 4v9.333A1.333 1.333 0 0111.333 14.667H4.667A1.333 1.333 0 013.333 13.333V4m2 0V2.667A1.333 1.333 0 016.667 1.333h2.666A1.333 1.333 0 0110.667 2.667V4" />
														</svg>
													</IconButton>
												</Box>
											</Box>
										</Box>
									)
								})}
							</Box>
						</Box>
					</Box>
				)}
			</Box>


			{/* Add/Edit Dialog */}
			<AddEditClientDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				merchant={merchant}
				editingClient={editingClient}
				onClientSaved={handleClientSaved}
			/>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Delete Client</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete "{getFullName(clientToDelete)}"?
						This will also delete all associated pets and cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmOpen(false)}>
						Cancel
					</Button>
					<Button
						onClick={confirmDelete}
						color="error"
						variant="contained"
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* More Options Menu — on mobile also covers the row actions dropped from the card (Add booking / Message) */}
			<Menu
				anchorEl={moreMenuAnchor}
				open={Boolean(moreMenuAnchor)}
				onClose={handleMoreClose}
				PaperProps={{
					sx: {
						mt: '4px',
						border: '1px solid #E2E8F0',
						borderRadius: '6px',
						minWidth: '150px'
					}
				}}
			>
				{isMobile && [
					<MenuItem
						key="add-booking"
						onClick={() => { handleAddBooking(moreMenuClient); handleMoreClose() }}
						sx={{ px: '16px', py: '8px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#2D3748', '&:hover': { bgcolor: '#F7FAFC' } }}
					>
						Add booking
					</MenuItem>,
					<MenuItem
						key="message"
						onClick={() => { handleMessageClient(moreMenuClient); handleMoreClose() }}
						sx={{ px: '16px', py: '8px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#2D3748', '&:hover': { bgcolor: '#F7FAFC' } }}
					>
						Message
					</MenuItem>
				]}
				<MenuItem
					onClick={handleEditFromMenu}
					sx={{
						px: '16px',
						py: '8px',
						fontFamily: 'Inter',
						fontSize: '14px',
						fontWeight: 400,
						color: '#2D3748',
						'&:hover': {
							bgcolor: '#F7FAFC'
						}
					}}
				>
					Edit client
				</MenuItem>
			</Menu>

			{/* Add Appointment Dialog */}
			<AddAppointmentDialog
				open={appointmentDialogOpen}
				onClose={() => {
					setAppointmentDialogOpen(false)
					setAppointmentClient(null)
				}}
				merchant={merchant}
				onAppointmentCreated={handleAppointmentCreated}
				prefilledClient={appointmentClient}
			/>

			{/* Import Clients & Pets Dialog */}
			<ImportClientsPetsDialog
				open={importDialogOpen}
				onClose={() => setImportDialogOpen(false)}
				merchant={merchant}
				onImportComplete={() => loadClients()}
			/>
		</Box>
	)
}

export default ClientsManager
